<template>
  <div>
    <div
      v-if="files.length > 0"
      class="list-group list-group-flush files-list col-md-6 pe-3"
    >
      <div
        v-for="(file, fileIdx) of files"
        :key="file.id"
        class="list-group-item"
      >
        <div class="row gx-2">
          <div class="col col-12 col-md mb-3">
            <label :class="['form-label', { required: file.isRequired }]">{{ fileLabel(file) }}</label>
            <input
              v-if="file.isStatic"
              type="text"
              class="form-control form-control-sm"
              readonly
              :value="file.filename"
            >
            <input
              v-else
              v-model="file.title"
              type="text"
              class="form-control form-control-sm"
              :placeholder="file.filename"
            >
          </div>
          <div class="col col-tight mb-3">
            <label class="form-label">&nbsp;</label>
            <div>
              <template v-if="file.isStatic">
                <button
                  v-if="staticFileIsPresent(file)"
                  type="button"
                  class="btn btn-sm btn-danger"
                  @click.prevent="showRemoveFilePrompt(fileIdx)"
                >
                  Delete
                </button>
                <label
                  v-else
                  :class="['btn btn-sm btn-success with-upload-icon text-nowrap', { 'disabled': !canUpload || isUploading }]"
                >
                  {{ $t('application_files.add_static_file') }}
                  <input
                    type="file"
                    style="display: none"
                    :disabled="!canUpload || isUploading"
                    @change="uploadApplicationFile($event, fileIdx, file)"
                  >
                </label>
              </template>
              <button
                v-else
                type="button"
                class="btn btn-sm btn-danger"
                @click.prevent="showRemoveFilePrompt(fileIdx)"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <label :class="['btn btn-sm btn-success', { 'with-upload-icon': !isUploading, 'disabled': !canUpload || isUploading }]">
      <template v-if="isUploading">
        <span
          class="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        />
        {{ uploadProgressMessage }}
      </template>
      <template v-else>
        {{ $t('application_files.add_file') }}
        <input
          type="file"
          style="display: none"
          :disabled="!canUpload || isUploading"
          @change="uploadApplicationFile"
        >
      </template>
    </label>

    <alert
      :message="uploadError"
      @dismiss="uploadError = null"
    />

    <teleport
      to="#vue-portal-target"
      append
    >
      <modal-confirmation-rails
        ref="removeFileConfirmationModal"
        modal-title="Delete file?"
        @confirmed="onRemoveFileConfirmed"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, useTemplateRef, watch} from 'vue'
import axios from 'axios'
import Rails from '@rails/ujs'

import ModalConfirmationRails from '../../vue-components/modal-confirmation-rails.vue'
import Alert from '../../vue-components/alert.vue'
import {isNonEmptyString} from '../../js-utils'

interface UploadFile {
  id: string;
  title: string;
  filename: string;
  isStatic: boolean;
  isRequired: boolean;
  type: string | null;
}

const props = defineProps({
  numberOfFiles: {type: Number, default: 5},
  uploadUrl: {type: String, default: null},
})

const removeFileConfirmationModal = useTemplateRef<InstanceType<typeof ModalConfirmationRails>>('removeFileConfirmationModal')

const isUploading = ref(false)
const uploadProgress = ref(0)
const uploadProgressMessage = ref('')
const uploadError = ref<string | null>(null)
const files = ref<UploadFile[]>([])
const filesJsonEl = ref<HTMLInputElement | null>(null)

const canUpload = computed(() => isNonEmptyString(props.uploadUrl))

const storeApplicationFiles = (): void => {
  if (filesJsonEl.value) {
    filesJsonEl.value.value = JSON.stringify(files.value)
  }
}

watch(
  files,
  () => {
    storeApplicationFiles()
  },
  {deep: true}
)

onMounted(() => {
  const jsonEl = document.querySelector('input[ref="applicationFiles"]')
  if (jsonEl instanceof HTMLInputElement) {
    filesJsonEl.value = jsonEl
    try {
      const filesData = JSON.parse(filesJsonEl.value.value)
      if (Array.isArray(filesData)) {
        files.value = filesData
      }
    } catch { /* empty */ }
  }
})

const uploadApplicationFile = (e: Event, fileIdx?: number, uploadFile?: UploadFile): void => {
  if (e.target instanceof HTMLInputElement) {
    uploadFileFromField(e.target, fileIdx, uploadFile)
  }
}

const uploadFileFromField = (uploadField: HTMLInputElement, fileIdx?: number, file?: UploadFile): void => {
  if (!canUpload.value) {
    return
  }

  if (!uploadField.files) {
    return
  }

  const uploadFile = uploadField.files.item(0)
  if (uploadFile === null) {
    // IE 11 will fire change event when setting uploadField.value to ''
    // if file is null, then do not anything
    return
  }

  const formData = new FormData()
  formData.append('file', uploadFile)
  if (isNonEmptyString(file?.type)) {
    formData.append('type_id', file.type)
  }

  // IE 11 will fire change event again
  uploadField.value = ''

  isUploading.value = true
  uploadProgress.value = 0
  uploadProgressMessage.value = 'Uploading file ...'
  uploadError.value = null

  axios.post(props.uploadUrl as string, formData, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': Rails.csrfToken()
    },
    onUploadProgress: (progressEvent) => {
      const total = progressEvent.total ?? progressEvent.loaded
      uploadProgress.value = Math.floor(progressEvent.loaded / total * 100)
      if (progressEvent.loaded === total) {
        uploadProgressMessage.value = 'Processing file ...'
      } else {
        uploadProgressMessage.value = `Uploading file: ${uploadProgress.value} %`
      }
    }
  })
    .then((response) => {
      if (response.data) {
        if (response.data.file) {
          if (fileIdx != null && fileIdx >= 0 && fileIdx <= files.value.length) {
            const file = files.value[fileIdx]
            file.id = response.data.file.id
            file.filename = response.data.file.filename
          } else {
            // check if we have a file with the same id
            const fileId = response.data.file.id
            let fileUpdated = false
            for (let i = 0; i < files.value.length; i++) {
              if (files.value[i].id === fileId) {
                files.value.splice(i, 1, response.data.file)
                fileUpdated = true
              }
            }
            if (!fileUpdated) {
              files.value.push(response.data.file)
            }
          }
        } else if (response.data.error) {
          uploadError.value = response.data.error
        } else {
          uploadError.value = 'Invalid response from server'
        }
      }
      isUploading.value = false
    })
    .catch((error) => {
      uploadError.value = error.message
      if (error.response && error.response.data && isNonEmptyString(error.response.data.error)) {
        uploadError.value = error.response.data.error
      }
      isUploading.value = false
    })
}

const showRemoveFilePrompt = (fileIdx: number): void => {
  removeFileConfirmationModal.value?.show(null, {
    title: 'Remove file?',
    message: 'Are you sure you want to remove this file?',
    type: 'danger',
    cancelActionTitle: 'Cancel',
    confirmActionTitle: 'Remove',
    confirmationCode: null,
    context: fileIdx
  })
}

const onRemoveFileConfirmed = (fileIdx: unknown): void => {
  removeFileConfirmationModal.value?.hide()

  if (typeof fileIdx == 'number' && fileIdx >= 0 && fileIdx < files.value.length) {
    const file = files.value[fileIdx]
    if (file.isStatic) {
      // for static files clear content
      file.id = ''
      file.filename = ''
    } else {
      files.value.splice(fileIdx, 1)
    }
  }
}

const fileLabel = (file: UploadFile): string => {
  if (file.isStatic) {
    return file.title
  }
  return 'File description'
}

const staticFileIsPresent = (file: UploadFile): boolean => {
  return isNonEmptyString(file.id)
}
</script>
